import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FactCheck as FactCheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { DRBFMCaseDesignChange } from '../../../types/drbfm';

interface DRBFMChangeReviewStepProps {
  changes: DRBFMCaseDesignChange[];
  isLoading: boolean;
  onChange: (changes: DRBFMCaseDesignChange[]) => void;
  onSubmit: (changes: DRBFMCaseDesignChange[]) => void | Promise<unknown>;
  onBack: () => void;
}

const DRBFMChangeReviewStep: React.FC<DRBFMChangeReviewStepProps> = ({
  changes,
  isLoading,
  onChange,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const updateChange = (
    index: number,
    field: 'itemName' | 'oldSpecification' | 'newSpecification' | 'changeDescription',
    value: string,
  ) => {
    onChange(changes.map((change, currentIndex) => (
      currentIndex === index ? { ...change, [field]: value } : change
    )));
  };

  const handleSubmit = async () => {
    await onSubmit(changes);
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('drbfm.case.reviewTitle', '変更点レビュー')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('drbfm.case.reviewDescription', '抽出された技術変更点をDRBFM入力として整えます。')}
        </Typography>
      </Box>

      {changes.length === 0 ? (
        <Typography color="text.secondary">
          {t('drbfm.case.noChanges', '抽出された変更点はありません。')}
        </Typography>
      ) : (
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {changes.map((change, index) => (
            <Stack key={change.id || index} spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" component="h3">
                  {t('drbfm.case.changeNumber', {
                    number: index + 1,
                    defaultValue: `変更点 ${index + 1}`,
                  })}
                </Typography>
                <Chip label={change.category} size="small" />
                {change.confidence != null && (
                  <Chip
                    label={t('drbfm.case.confidence', {
                      value: Math.round(change.confidence * 100),
                      defaultValue: `信頼度 ${Math.round(change.confidence * 100)}%`,
                    })}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              <TextField
                label={t('drbfm.case.changedItem', '変更項目')}
                value={change.itemName}
                onChange={(event) => updateChange(index, 'itemName', event.target.value)}
                disabled={isLoading}
                fullWidth
                size="small"
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('drbfm.case.oldSpecification', '旧仕様')}
                  value={change.oldSpecification}
                  onChange={(event) => updateChange(index, 'oldSpecification', event.target.value)}
                  disabled={isLoading}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t('drbfm.case.newSpecification', '新仕様')}
                  value={change.newSpecification}
                  onChange={(event) => updateChange(index, 'newSpecification', event.target.value)}
                  disabled={isLoading}
                  fullWidth
                  size="small"
                />
              </Stack>

              <TextField
                label={t('drbfm.case.technicalChange', '技術変更内容')}
                value={change.changeDescription}
                onChange={(event) => updateChange(index, 'changeDescription', event.target.value)}
                disabled={isLoading}
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          disabled={isLoading}
          onClick={onBack}
        >
          {t('drbfm.case.backToConditions', 'ケース条件に戻る')}
        </Button>
        <Button
          variant="contained"
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />
          }
          disabled={isLoading || changes.length === 0}
          onClick={handleSubmit}
        >
          {isLoading
            ? t('drbfm.case.generatingPrecheck', '事前確認を生成中')
            : t('drbfm.case.continueToPrecheck', '事前確認へ進む')}
        </Button>
      </Box>
    </Stack>
  );
};

export default DRBFMChangeReviewStep;
